import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Recycle, MapPin, CreditCard, Phone, Gift, CheckCircle } from "lucide-react";

const CompleteProfilePage = () => {
  const { user, completeProfile } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    dni: "",
    address: "",
    postal_code: "",
    phone: ""
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePostalCodeChange = (value) => {
    setFormData(prev => ({ ...prev, postal_code: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await completeProfile(formData);
      toast.success("¡Perfil completado! Has recibido 100 puntos de bienvenida");
      navigate("/catalogo", { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || "Error al completar el perfil";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2">
              <div className="w-12 h-12 bg-[#374B67] rounded-xl flex items-center justify-center">
                <Recycle className="w-7 h-7 text-white" />
              </div>
              <div>
                <span className="font-bold text-[#374B67] text-xl">Reutiliza</span>
                <span className="text-[#69C7EA] font-semibold text-xl"> Majadahonda</span>
              </div>
            </div>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center pb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-[#374B67]">¡Casi listo!</CardTitle>
              <CardDescription>
                Hola {user?.name}, completa tu perfil para poder usar la plataforma
              </CardDescription>
              {/* Welcome Points Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full mx-auto mt-2">
                <Gift className="w-4 h-4 text-green-600" />
                <span className="text-sm font-semibold text-green-700">+100 puntos al completar</span>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* DNI */}
                <div className="space-y-2">
                  <Label htmlFor="dni">DNI/NIE</Label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="dni"
                      name="dni"
                      type="text"
                      placeholder="12345678A"
                      value={formData.dni}
                      onChange={handleChange}
                      className="pl-10 uppercase"
                      required
                      data-testid="dni-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500">Formato: 12345678A o X1234567A</p>
                </div>

                {/* Address */}
                <div className="space-y-2">
                  <Label htmlFor="address">Dirección</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="address"
                      name="address"
                      type="text"
                      placeholder="Calle, número, piso"
                      value={formData.address}
                      onChange={handleChange}
                      className="pl-10"
                      required
                      data-testid="address-input"
                    />
                  </div>
                </div>

                {/* Postal Code */}
                <div className="space-y-2">
                  <Label htmlFor="postal_code">Código Postal</Label>
                  <Select value={formData.postal_code} onValueChange={handlePostalCodeChange} required>
                    <SelectTrigger data-testid="postal-code-select">
                      <SelectValue placeholder="Seleccionar código postal" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="28220">28220 - Majadahonda</SelectItem>
                      <SelectItem value="28221">28221 - Majadahonda</SelectItem>
                      <SelectItem value="28222">28222 - Majadahonda</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Solo para residentes de Majadahonda</p>
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone">Teléfono (opcional)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="600123456"
                      value={formData.phone}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-[#374B67] hover:bg-[#2C3E56] text-base"
                  disabled={loading}
                  data-testid="complete-profile-btn"
                >
                  {loading ? "Guardando..." : "Completar Perfil"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfilePage;
